/**
 * ModelSelectionDialog - Modal dialog for selecting AI models
 *
 * Features:
 * - Search/filter models
 * - Category tabs (All, Featured, Gemini, Claude, etc.)
 * - Model cards with details (name, description, pricing, capabilities)
 * - Dark theme matching Google AI Studio style
 */

import { memo, useState, useCallback, useMemo } from 'react';

// ============================================================================
// TYPES
// ============================================================================

interface ModelInfo {
  /** Model ID for API calls */
  id: string;
  /** Display name */
  name: string;
  /** Model family/provider */
  provider: 'anthropic' | 'google' | 'openai';
  /** Short description */
  description: string;
  /** Is this model new? */
  isNew?: boolean;
  /** Is this a paid/premium model? */
  isPaid?: boolean;
  /** Capabilities/features */
  capabilities?: string[];
  /** Knowledge cutoff date */
  knowledgeCutoff?: string;
  /** Context window size */
  contextWindow?: string;
}

interface ModelSelectionDialogProps {
  /** Whether the dialog is open */
  isOpen: boolean;
  /** Called when dialog should close */
  onClose: () => void;
  /** Called when a model is selected */
  onSelect: (modelId: string) => void;
  /** Currently selected model ID */
  selectedModelId?: string;
}

// ============================================================================
// MODEL DATA
// ============================================================================

const AVAILABLE_MODELS: ModelInfo[] = [
  // Anthropic Models
  {
    id: 'claude-sonnet-4-5-20250929',
    name: 'Claude Sonnet 4.5',
    provider: 'anthropic',
    description: 'Best balance of speed, intelligence, and cost. Excellent for most tasks.',
    isNew: true,
    capabilities: ['Text', 'Code', 'Vision', 'Analysis'],
    knowledgeCutoff: 'Apr 2025',
    contextWindow: '200K tokens',
  },
  {
    id: 'claude-opus-4-5-20250929',
    name: 'Claude Opus 4.5',
    provider: 'anthropic',
    description: 'Most capable model for complex reasoning and creative tasks.',
    isNew: true,
    isPaid: true,
    capabilities: ['Text', 'Code', 'Vision', 'Analysis', 'Extended Thinking'],
    knowledgeCutoff: 'Apr 2025',
    contextWindow: '200K tokens',
  },
  {
    id: 'claude-3-5-haiku-20241022',
    name: 'Claude 3.5 Haiku',
    provider: 'anthropic',
    description: 'Fastest Claude model. Great for quick tasks and high-volume use.',
    capabilities: ['Text', 'Code', 'Fast'],
    knowledgeCutoff: 'Apr 2024',
    contextWindow: '200K tokens',
  },
  // Google Models
  {
    id: 'gemini-2.0-flash',
    name: 'Gemini 2.0 Flash',
    provider: 'google',
    description: 'Fast multimodal model with excellent speed and quality balance.',
    isNew: true,
    capabilities: ['Text', 'Code', 'Vision', 'Audio'],
    knowledgeCutoff: 'Jan 2025',
    contextWindow: '1M tokens',
  },
  {
    id: 'gemini-2.0-flash-thinking-exp',
    name: 'Gemini 2.0 Flash Thinking',
    provider: 'google',
    description: 'Extended thinking mode for complex reasoning tasks.',
    isNew: true,
    capabilities: ['Text', 'Code', 'Extended Thinking'],
    knowledgeCutoff: 'Jan 2025',
    contextWindow: '1M tokens',
  },
  {
    id: 'gemini-1.5-pro',
    name: 'Gemini 1.5 Pro',
    provider: 'google',
    description: 'Powerful multimodal model with massive context window.',
    capabilities: ['Text', 'Code', 'Vision', 'Audio', 'Video'],
    knowledgeCutoff: 'Nov 2024',
    contextWindow: '2M tokens',
  },
];

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'featured', label: 'Featured' },
  { id: 'anthropic', label: 'Claude' },
  { id: 'google', label: 'Gemini' },
] as const;

// ============================================================================
// ICONS
// ============================================================================

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const SearchIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const ClaudeIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" />
  </svg>
);

const GeminiIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2L9.19 8.63L2 9.24l5.46 4.73L5.82 21L12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2z" />
  </svg>
);

const CheckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// ============================================================================
// HELPER COMPONENTS
// ============================================================================

interface ModelCardProps {
  model: ModelInfo;
  isSelected: boolean;
  onSelect: () => void;
}

function ModelCard({ model, isSelected, onSelect }: ModelCardProps) {
  const ProviderIcon = model.provider === 'anthropic' ? ClaudeIcon : GeminiIcon;
  const providerColor = model.provider === 'anthropic' ? 'text-orange-400' : 'text-blue-400';

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`
        w-full text-left p-4 rounded-xl border transition-all
        ${isSelected
          ? 'bg-slate-700/50 border-amber-500'
          : 'bg-slate-800/50 border-slate-700 hover:border-slate-600 hover:bg-slate-700/30'
        }
      `}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 flex-1 min-w-0">
          {/* Provider Icon */}
          <div className={`mt-0.5 ${providerColor}`}>
            <ProviderIcon />
          </div>

          {/* Model Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-medium text-white">{model.name}</span>
              {model.isNew && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium bg-green-500/20 text-green-400 rounded">
                  New
                </span>
              )}
              {model.isPaid && (
                <span className="px-1.5 py-0.5 text-[10px] font-medium bg-amber-500/20 text-amber-400 rounded">
                  Paid
                </span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-0.5 font-mono">{model.id}</p>
            <p className="text-sm text-slate-400 mt-2 line-clamp-2">{model.description}</p>

            {/* Capabilities */}
            {model.capabilities && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {model.capabilities.map((cap) => (
                  <span
                    key={cap}
                    className="px-1.5 py-0.5 text-[10px] text-slate-400 bg-slate-700/50 rounded"
                  >
                    {cap}
                  </span>
                ))}
              </div>
            )}

            {/* Context & Cutoff */}
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
              {model.contextWindow && <span>{model.contextWindow}</span>}
              {model.knowledgeCutoff && <span>Cutoff: {model.knowledgeCutoff}</span>}
            </div>
          </div>
        </div>

        {/* Selection Indicator */}
        {isSelected && (
          <div className="text-amber-400 mt-1">
            <CheckIcon />
          </div>
        )}
      </div>
    </button>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const ModelSelectionDialog = memo(function ModelSelectionDialog({
  isOpen,
  onClose,
  onSelect,
  selectedModelId,
}: ModelSelectionDialogProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');

  // Filter models based on search and category
  const filteredModels = useMemo(() => {
    return AVAILABLE_MODELS.filter((model) => {
      // Category filter
      if (activeCategory !== 'all' && activeCategory !== 'featured') {
        if (model.provider !== activeCategory) return false;
      }
      if (activeCategory === 'featured') {
        if (!model.isNew) return false;
      }

      // Search filter
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        return (
          model.name.toLowerCase().includes(query) ||
          model.id.toLowerCase().includes(query) ||
          model.description.toLowerCase().includes(query)
        );
      }

      return true;
    });
  }, [searchQuery, activeCategory]);

  const handleSelect = useCallback(
    (modelId: string) => {
      onSelect(modelId);
      onClose();
    },
    [onSelect, onClose]
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-50"
        onClick={onClose}
      />

      {/* Dialog */}
      <div className="fixed inset-4 md:inset-auto md:top-1/2 md:left-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[600px] md:max-h-[80vh] bg-slate-900 rounded-2xl shadow-2xl z-50 flex flex-col overflow-hidden border border-slate-700">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700">
          <h2 className="text-lg font-semibold text-white">Model selection</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1 rounded-lg text-slate-400 hover:text-white hover:bg-slate-700 transition-colors"
          >
            <CloseIcon />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3 border-b border-slate-700">
          <div className="relative">
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">
              <SearchIcon />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for a model"
              className="w-full pl-10 pr-4 py-2 text-sm text-white bg-slate-800 rounded-lg border border-slate-700 placeholder:text-slate-500 focus:border-slate-600 focus:outline-none"
            />
          </div>
        </div>

        {/* Category Tabs */}
        <div className="px-5 py-3 border-b border-slate-700">
          <div className="flex items-center gap-2 overflow-x-auto">
            {CATEGORIES.map((cat) => (
              <button
                key={cat.id}
                type="button"
                onClick={() => setActiveCategory(cat.id)}
                className={`
                  px-3 py-1.5 text-sm rounded-full whitespace-nowrap transition-colors
                  ${activeCategory === cat.id
                    ? 'bg-slate-700 text-white'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                  }
                `}
              >
                {cat.label}
              </button>
            ))}
          </div>
        </div>

        {/* Model List */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {filteredModels.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              No models found matching your search.
            </div>
          ) : (
            filteredModels.map((model) => (
              <ModelCard
                key={model.id}
                model={model}
                isSelected={model.id === selectedModelId}
                onSelect={() => handleSelect(model.id)}
              />
            ))
          )}
        </div>
      </div>
    </>
  );
});
